import type { ReactNode, HTMLAttributes } from "react";

import { cn } from "@/lib/cn";
import { Reveal } from "@/components/Reveal";

interface SectionProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  /** Semantic landmark tag. Defaults to <section>. */
  as?: "section" | "article" | "aside";
  /** Adds container + vertical spacing when true (default). */
  contained?: boolean;
  /** Soft surface background. */
  tone?: "default" | "cream" | "ink";
  eyebrow?: ReactNode;
  heading?: ReactNode;
  headingLevel?: 2 | 3;
  intro?: ReactNode;
}

/**
 * Semantic section wrapper used across all pages. Accepts an eyebrow tag,
 * heading, and intro copy in a consistent, accessible rhythm.
 *
 * Keep headings hierarchical: pages render h1 at the top; sections default
 * to h2. For nested subsections, pass headingLevel={3}.
 */
export function Section({
  children,
  as: As = "section",
  contained = true,
  tone = "default",
  eyebrow,
  heading,
  headingLevel = 2,
  intro,
  className,
  ...rest
}: SectionProps) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const toneClass = {
    default: "bg-background",
    cream: "bg-cream-contrast dark:bg-muted",
    ink: "bg-ink text-cream dark:bg-background dark:text-foreground dark:border-t dark:border-b dark:border-border",
  }[tone];

  return (
    <As className={cn(toneClass, "py-20 sm:py-24", className)} {...rest}>
      <div className={cn(contained && "container")}>
        {(eyebrow || heading || intro) && (
          <Reveal>
            <header className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
              {eyebrow && (
                <p className="mb-3 inline-flex items-center rounded-full bg-coral-50 px-3 py-1 text-xs font-medium uppercase tracking-wide text-coral-700">
                  {eyebrow}
                </p>
              )}
              {heading && (
                <Heading
                  className={cn(
                    "text-display-md sm:text-display-lg font-display text-balance",
                    tone === "ink" ? "text-cream dark:text-foreground" : "text-ink dark:text-foreground",
                  )}
                >
                  {heading}
                </Heading>
              )}
              {intro && (
                <p
                  className={cn(
                    "mt-4 text-lg leading-relaxed text-balance",
                    tone === "ink" ? "text-cream/80 dark:text-muted-foreground" : "text-muted-foreground",
                  )}
                >
                  {intro}
                </p>
              )}
            </header>
          </Reveal>
        )}
        <Reveal delay="delay-150">
          {children}
        </Reveal>
      </div>
    </As>
  );
}
